import { FC, useCallback, useMemo, useState, type JSX } from 'react';

import { Button, Modal, Text } from '@payaca/ambrosia';
import BasicField from '@payaca/components/basicField/BasicField';
import CurrencyField from '@payaca/components/currencyField/CurrencyField';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { useDeal } from '@payaca/store/hooks/appState';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import './CreateInvoiceControl.sass';

import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import { useRegion } from '@/api/rest/users/useRegion';
import FeedbackBlock from '@payaca/components/feedbackBlock/FeedbackBlock';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import SplitBarVisualisation from '@payaca/components/splitBarVisualisation/SplitBarVisualisation';
import * as colours from '@payaca/constants/colours';
import { getNumericalRangeFieldValidator } from '@payaca/helpers/fieldValidationHelper';
import {
  getCustomerInvoicedValueIncludingDrafts,
  getMaxInvoiceableValue,
} from '@payaca/helpers/grantHelper';
import { currencyPrice, formatPercentage } from '@payaca/helpers/financeHelper';
import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import * as invoiceActions from '@payaca/store/invoices/invoicesActions';
import { AccountRegions } from '@payaca/types/accountTypes';
import { FeedbackLevel } from '@payaca/types/feedbackTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import { useDispatch } from 'react-redux';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

type Props = {
  onCreateInvoiceSuccess?: (invoiceId: number) => void;
  dealId: number;
  defaultToInvoiceValue?: number;
};
const CreateInvoiceControl: FC<Props> = ({
  onCreateInvoiceSuccess,
  dealId,
  defaultToInvoiceValue,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const deal = useDeal(dealId);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [createInvoiceErrorMessage, setCreateInvoiceErrorMessage] =
    useState<string>();

  const [inputPercentage, setInputPercentage] = useState(true);

  const region = useRegion();
  const { acceptedValue, invoicedValue } = useMemo(() => {
    if (!deal) {
      return {
        acceptedValue: 0,
        invoicedValue: 0,
      };
    }

    return deal;
  }, [deal]);

  // The percentage stays based on the pre-grant total (acceptedValue), but the
  // invoiceable amount is capped at the grant-net "to invoice" value.
  const { project } = useGetProjectByIdOverview(deal?.publicId);
  const totals = project?.totals;
  const totalPayable = totals?.totalPayable.value ?? acceptedValue;
  // `totals` arrives whole or not at all, so fall back whole rather than
  // per field.
  const invoiceTotals = totals && {
    invoicedValue: totals.invoicedValue.value,
    sentInvoicedValue: totals.sentInvoicedValue.value,
    customerInvoicedValue: totals.customerInvoicedValue.value,
  };
  const customerInvoiced = invoiceTotals
    ? getCustomerInvoicedValueIncludingDrafts(invoiceTotals)
    : invoicedValue;
  const grantTotal = Math.max(0, acceptedValue - totalPayable);
  const hasGrants = grantTotal > 0;
  const invoicedForBar = customerInvoiced;
  const maxInvoiceableValue = invoiceTotals
    ? getMaxInvoiceableValue({ totalPayable, ...invoiceTotals })
    : Math.max(0, acceptedValue - invoicedValue);

  const initialFormState = useMemo(() => {
    const invoiceValue = Math.min(
      defaultToInvoiceValue || maxInvoiceableValue,
      maxInvoiceableValue
    );
    return {
      invoiceValue: invoiceValue,
      invoicePercentage:
        Math.round((invoiceValue / acceptedValue) * 10000) / 100,
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'defaultToInvoiceValue'
  }, [acceptedValue, maxInvoiceableValue]);

  const fieldValidators = useMemo(() => {
    const maxValue = maxInvoiceableValue;

    return {
      invoiceValue: [
        getNumericalRangeFieldValidator(
          -1,
          maxValue + 1,
          undefined,
          `Invoice value cannot be less than zero`,
          `Invoice value cannot exceed ${currencyPrice(maxValue, region)}`
        ),
      ],
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'region'
  }, [maxInvoiceableValue]);

  const onSubmit = useCallback(
    (invoiceValue: number) => {
      if (isCreatingInvoice) return;
      setIsCreatingInvoice(true);
      setCreateInvoiceErrorMessage(undefined);
      dispatch(
        invoiceActions.requestCreateInvoice(
          {
            dealId: dealId,
            // With grants the "uninvoiced deal value" shortcut is pre-grant, so
            // always send the explicit grant-net value instead.
            createForUninvoicedDealValue:
              !hasGrants && invoiceValue === maxInvoiceableValue,
            invoiceValue: invoiceValue,
          },
          (invoiceId) => {
            onCreateInvoiceSuccess?.(invoiceId);
            setIsCreatingInvoice(false);
          },
          () => {
            setCreateInvoiceErrorMessage(
              'Something went wrong creating the invoice'
            );
            setIsCreatingInvoice(false);
          }
        )
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dealId', and 'dispatch'
    [onCreateInvoiceSuccess, hasGrants, maxInvoiceableValue, isCreatingInvoice]
  );

  const currencySymbol = getRegionalTextString(
    region || AccountRegions.UK,
    RegionalStrings.CURRENCY_SYMBOL
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <>
          <Modal.Body className="form-contents">
            <SplitBarVisualisation
              data={[
                {
                  label: 'Invoiced',
                  value: invoicedForBar,
                  id: 'invoiced',
                  colour: colours.PURPLE_LIGHT,
                },
                {
                  label: 'This invoice',
                  value: formState.invoiceValue,
                  id: 'this-invoice',
                  colour: colours.PURPLE_DARK,
                },
                {
                  label: 'Remaining',
                  value:
                    acceptedValue -
                    invoicedForBar -
                    formState.invoiceValue -
                    grantTotal,
                  id: 'remaining',
                  colour: colours.GREY_LIGHTER,
                },
                ...(hasGrants
                  ? [
                      {
                        label: 'Grants',
                        value: grantTotal,
                        id: 'grants',
                        colour: colours.GREEN,
                      },
                    ]
                  : []),
              ]}
              formatValue={(value) => {
                return `${formatPercentage((value / acceptedValue) * 100)}%`;
              }}
              renderPopperContent={(dataElement) => (
                <dl className="split-bar-popper-content">
                  <dt>{dataElement.label}:</dt>
                  <dd>
                    <strong>
                      {formatPercentage(
                        (dataElement.value / acceptedValue) * 100
                      )}
                      % ({currencyPrice(dataElement.value, region)})
                    </strong>
                  </dd>
                </dl>
              )}
            />
            <div>
              <div className="invoice-value-label-container">
                <label
                  htmlFor="invoice-percentage-field"
                  className="invoice-value-label"
                >
                  Invoice value:
                </label>
              </div>

              <ValidatedFieldWrapper
                validationResult={validationState?.invoiceValue}
              >
                <div className="value-control">
                  {inputPercentage && (
                    <>
                      <PermissionGuard
                        renderIfHasPermissions={[
                          InvoicesPermissions.EDIT_INVOICE_VALUE,
                        ]}
                      >
                        <BasicField
                          className="invoice-percentage-field"
                          styleVariant={InputStyleVariant.STANDARD}
                          value={formState.invoicePercentage}
                          name="invoicePercentage"
                          onChange={(value) => {
                            const invoicePercentage = +value.invoicePercentage;
                            onFieldChange({
                              invoicePercentage: invoicePercentage,
                              invoiceValue: Math.round(
                                (acceptedValue * invoicePercentage) / 100
                              ),
                            });
                          }}
                          type="number"
                          additionalInputProps={{
                            step: 0.1,
                            id: 'invoice-percentage-field',
                          }}
                          iconAfter={'percent-02.3'}
                        />
                      </PermissionGuard>
                      <span>
                        <PermissionGuard
                          renderIfMissingPermissions={[
                            InvoicesPermissions.EDIT_INVOICE_VALUE,
                          ]}
                        >
                          {formState.invoicePercentage}%{' '}
                        </PermissionGuard>
                        of Project value {currencyPrice(acceptedValue, region)}
                      </span>
                      <div className="connector"></div>
                      <span className="invoice-value">
                        {currencyPrice(formState.invoiceValue, region)}
                      </span>
                    </>
                  )}
                  {!inputPercentage && (
                    <>
                      <span>
                        {formState.invoicePercentage}% of Project value{' '}
                        {currencyPrice(acceptedValue, region)}
                      </span>
                      <div className="connector"></div>

                      <CurrencyField
                        styleVariant={InputStyleVariant.STANDARD}
                        value={formState.invoiceValue}
                        name="invoiceValue"
                        onChange={(value) => {
                          const invoiceValue = value.invoiceValue;

                          const invoicePercentage =
                            Math.round((invoiceValue / acceptedValue) * 10000) /
                            100;

                          onFieldChange({
                            invoicePercentage: invoicePercentage,
                            invoiceValue: invoiceValue,
                          });
                        }}
                      />
                    </>
                  )}
                </div>
              </ValidatedFieldWrapper>
              <PermissionGuard
                renderIfHasPermissions={[
                  InvoicesPermissions.EDIT_INVOICE_VALUE,
                ]}
              >
                <div className="button-container">
                  <Button
                    className="ml-auto"
                    variant="link"
                    onClick={() => setInputPercentage((x) => !x)}
                  >
                    Input {inputPercentage ? currencySymbol : '%'}
                  </Button>
                </div>
              </PermissionGuard>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                disabled={!isValid}
                isProcessing={isCreatingInvoice}
                onClick={() => onSubmit(formState.invoiceValue)}
              >
                Create for {currencyPrice(formState.invoiceValue, region)}
              </Button>
              {!!createInvoiceErrorMessage?.length && (
                <FeedbackBlock feedbackLevel={FeedbackLevel.ERROR}>
                  <span>{createInvoiceErrorMessage}</span>
                </FeedbackBlock>
              )}
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'createInvoiceErrorMessage', 'currencySymbol', 'region', and 'createInvoiceErrorMessage.length'
    [
      acceptedValue,
      invoicedForBar,
      grantTotal,
      hasGrants,
      onSubmit,
      isCreatingInvoice,
      inputPercentage,
    ]
  );

  if (hasGrants && maxInvoiceableValue === 0) {
    return (
      <div className="create-invoice-control">
        <Modal.Body>
          <Text>
            Nothing is left to invoice the customer once grants are deducted.
          </Text>
        </Modal.Body>
      </div>
    );
  }

  return (
    <div className="create-invoice-control">
      {maxInvoiceableValue === 0 ? (
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              isProcessing={isCreatingInvoice}
              replaceContentWhenProcessing
              onClick={() => onSubmit(0)}
            >
              Create for {currencyPrice(0, region)}
            </Button>
            {!!createInvoiceErrorMessage?.length && (
              <FeedbackBlock feedbackLevel={FeedbackLevel.ERROR}>
                <span>{createInvoiceErrorMessage}</span>
              </FeedbackBlock>
            )}
          </Modal.Footer.Actions>
        </Modal.Footer>
      ) : (
        <ValidatedForm
          initialFormState={initialFormState}
          renderFormContents={renderFormContents}
          fieldValidators={fieldValidators}
        />
      )}
    </div>
  );
};
export default CreateInvoiceControl;
